// ============================================================================
// Utilidades de lectura de documentos con IA (Claude, vía Cloudflare Functions).
// - fileADatosIA: prepara el archivo (imágenes reescaladas a máx 1600px como JPEG,
//   PDFs tal cual) → { media_type, data } en base64.
// - analizarPolizaIA: lee la carátula de una póliza (/api/analizar-poliza) y
//   devuelve { ok, datos } o { ok:false, error } con mensaje listo para mostrar.
// La persona SIEMPRE revisa lo autocompletado antes de guardar.
// ============================================================================
import { ofrecerPaqueteIA } from './iaCupo';
import { postIA } from './apiIA';

// Empareja un texto detectado por la IA (aseguradora, ramo, subramo) contra un catálogo
// por nombre: normaliza acentos/símbolos; exacto > contenido > tokens compartidos.
// Devuelve el item o null. Lo usan Pólizas (crear con IA) y Solicitudes (convertir).
export function matchCatalogo(texto, lista) {
  const norm = s => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  const STOP = new Set(['seguros','seguro','compania','de','la','el','y','sa','s','a','aseguradora','cia','inc','internacional','panama']);
  const tokens = s => norm(s).split(' ').filter(t => t.length > 2 && !STOP.has(t));
  const nt = norm(texto); if (!nt) return null;
  let best = null, bestScore = 0;
  for (const item of (lista || [])) {
    const nn = norm(item.nombre); if (!nn) continue;
    let score = 0;
    if (nn === nt) score = 100;
    else if (nt.includes(nn)) score = 50 + nn.length;
    else if (nn.includes(nt)) score = 40 + nt.length;
    else {
      const shared = tokens(item.nombre).filter(t => tokens(texto).includes(t));
      if (shared.length) score = shared.length * 10;
    }
    if (score > bestScore) { bestScore = score; best = item; }
  }
  return bestScore >= 10 ? best : null;
}

export const fileADatosIA = (file) => new Promise((resolve, reject) => {
  if (file.type.startsWith('image/')) {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const max = 1600;
        let { width, height } = img;
        if (width > max || height > max) {
          const r = Math.min(max / width, max / height);
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve({ media_type: 'image/jpeg', data: canvas.toDataURL('image/jpeg', 0.85).split(',')[1] });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  } else {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => resolve({ media_type: file.type || 'application/pdf', data: String(reader.result).split(',')[1] });
    reader.readAsDataURL(file);
  }
});

// El mensaje que ve la persona SIEMPRE debe decir por qué falló.
const MOTIVOS = {
  no_configurado: 'La lectura con IA aún no está activada en tu sistema.',
  cupo: 'Llegaste al límite de análisis con IA de tu plan este mes.',
  api_error: 'El servicio de IA devolvió un error. Intenta de nuevo.',
  sin_json: 'No se pudieron leer datos claros del documento. Prueba con un archivo más nítido.',
};

// Decide la prima NETA (sin impuesto, ya con descuentos) a partir de lo que leyó
// la IA, y la cuadra contra el total. La carátula suele traer "Prima 900 ·
// Descuento 100 · Prima neta 800 · Impuesto · Total": si la IA se quedó con la de
// antes del descuento, el total la delata. Devuelve { prima, imp, aviso }.
//   impDefault = % del ramo (6 auto, 5 los demás) si el documento no trae el %.
export function primaNetaIA(d, impDefault) {
  const num = v => (v == null || v === '' || isNaN(Number(v)) ? null : Number(v));
  const r2 = n => Math.round(n * 100) / 100;
  const pctLeido = num(d.impuesto_pct);
  const imp = pctLeido != null && pctLeido >= 0 && pctLeido <= 20 ? pctLeido : (num(impDefault) ?? 5);
  let prima = num(d.prima);
  const total = num(d.prima_total), bruta = num(d.prima_bruta);
  const desc = num(d.descuento) || 0, rec = num(d.recargo) || 0, impMonto = num(d.impuesto_monto);
  let aviso = null;

  // Tomó la prima de antes del descuento → réstalo.
  if (prima != null && bruta != null && (desc || rec) && Math.abs(prima - bruta) < 0.01) {
    prima = r2(bruta - desc + rec);
    aviso = `Se aplicó el descuento/recargo de la carátula: prima ${bruta} → ${prima}.`;
  }
  if (prima == null && bruta != null) prima = r2(bruta - desc + rec);

  // Cuadre contra el total con impuesto: manda el total.
  if (total != null && total > 0) {
    const desdeTotal = impMonto != null && impMonto >= 0 && impMonto < total
      ? r2(total - impMonto)
      : r2(total / (1 + imp / 100));
    if (prima == null) prima = desdeTotal;
    else if (Math.abs(r2(prima * (1 + imp / 100)) - total) > 1) {
      aviso = `La prima leída (${prima}) no cuadra con el total ${total} e impuesto ${imp}%; se usó ${desdeTotal} (prima neta después de descuentos).`;
      prima = desdeTotal;
    }
  }
  return { prima, imp, aviso };
}

export async function analizarPolizaIA(file) {
  try {
    const { media_type, data } = await fileADatosIA(file);
    const pedir = () => postIA('/api/analizar-poliza', { media_type, data });
    let res = await pedir();
    // Cupo agotado → ampliarlo de un clic y reintentar solo.
    if (!res.ok && res.reason === 'cupo' && await ofrecerPaqueteIA(res.detalle)) {
      res = await pedir();
    }
    if (!res.ok) {
      // `detalle` trae el motivo del servidor (ej. el aviso de cupo del proxy de IA).
      if (res.reason === 'cupo' && res.detalle) return { ok: false, error: res.detalle };
      const base = MOTIVOS[res.reason] || 'No se pudo leer la póliza.';
      return { ok: false, error: base + (res.detalle ? ` (${res.detalle})` : '') };
    }
    return { ok: true, datos: res.datos || {} };
  } catch (e) {
    return { ok: false, error: 'No se pudo procesar el archivo: ' + (e.message || e) };
  }
}
